import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, LayoutGrid, List, FileText, FileSpreadsheet, ChevronDown, ChevronLeft, ChevronRight, FilePlus2, Tag, CopySlash, Copy, Printer, Trash2, Star, CheckCircle2, Link2, ScanBarcode } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { useQuote } from "@/context/QuoteContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { TagPickerDialog } from "@/components/TagPickerDialog";
import { ProductLinkDialog } from "@/components/ProductLinkDialog";
import { DuplicateFinderDialog } from "@/components/products/DuplicateFinderDialog";
import { ImportExcelDialog } from "@/components/ImportExcelDialog";
import { QuotePicker } from "@/components/QuotePicker";
import { ProductFilters } from "@/components/products/ProductFilters";
import { ProductsGrid } from "@/components/products/ProductsGrid";
import { ProductsTable } from "@/components/products/ProductsTable";
import { TagManagerDialog } from "@/components/products/TagManagerDialog";
import { MobileProductsList } from "@/components/products/MobileProductsList";
import { MobileProductFilters } from "@/components/products/MobileProductFilters";
import { BarcodeScannerDialog } from "@/components/products/BarcodeScannerDialog";

export default function ProductsList() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const role = user?.role;
  const isFactory = user?.role === "factory"; // 工厂 role: read-only product catalogue
  const isCustomer = user?.role === "customer"; // 客户 role: read-only product catalogue (filtered to products with category)
  const isProductManager = user?.role === "product_manager"; // 产品管理员 (2026-02-27): read-only on the products library.
  const isReadOnly = isFactory || isCustomer || isProductManager;
  const isAdmin = user?.role === "admin"; // deleting products is admin-only
  // 2026-06 · 采购/采购经理 batch menu keeps ONLY 设置标签 + 复制产品 (user spec).
  const isBuyerRole = ["buyer", "procurement_manager"].includes(user?.role);
  const { addToQuote, activeQuote } = useQuote();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read filters back from the URL so returning from a product edit restores
  // the exact same view (search / filters / sort / page).
  const urlGet = (k, fb) => searchParams.get(k) ?? fb;
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState(urlGet("search", ""));
  const [loading, setLoading] = useState(true);
  // Customer view forces GRID mode (visual catalogue per 2026-02 spec screenshot).
  const [view, setView] = useState(isCustomer ? "grid" : (localStorage.getItem("quotesys_products_view") || "list"));
  // 2026-02-09 · Grid density preference — "compact" packs 6-8 cards/row and
  // hides descriptions & price table; "comfortable" keeps the classic layout.
  const [density, setDensity] = useState(localStorage.getItem("quotesys_products_density") || "comfortable");
  const switchDensity = (d) => { setDensity(d); localStorage.setItem("quotesys_products_density", d); };
  // 2026-02 · Product-type default: on a fresh landing (URL doesn't set
  // ``show_all`` or ``product_type``) show the user's 我的产品 filter. Users can
  // pick a specific type from the dropdown OR press "Show All" to clear it.
  // 2026-02 (later) — "我的收藏" default overrides: fresh landing filters to the
  // user's favorites unless they clicked "Show All" or picked a product_type.
  const isFreshLanding = !searchParams.has("show_all")
    && !searchParams.has("product_type")
    && !searchParams.has("favorites")
    && !searchParams.has("recent")
    && !searchParams.has("new_only")
    && !searchParams.has("mine");
  const initialProductType = searchParams.has("product_type")
    ? searchParams.get("product_type")
    : (searchParams.has("show_all") ? "" : "");
  const initialFavorites = searchParams.has("favorites")
    ? searchParams.get("favorites") === "1"
    : isFreshLanding;
  const initialRecent = searchParams.get("recent") === "1";
  const initialMine = searchParams.get("mine") === "1";
  const initialNewOnly = searchParams.get("new_only") === "1";
  const [filters, setFilters] = useState({
    category: urlGet("category", ""),
    subcategory: urlGet("subcategory", ""),
    brand: urlGet("brand", ""),
    buyer: urlGet("buyer", ""),
    seller: urlGet("seller", ""),
    product_type: initialProductType,
    favorites: initialFavorites,
    recent: initialRecent,
    mine: initialMine,
    new_only: initialNewOnly,
    date_from: urlGet("date_from", ""),
    date_to: urlGet("date_to", ""),
    cost_min: urlGet("cost_min", ""),
    cost_max: urlGet("cost_max", ""),
    cost_currency: urlGet("cost_currency", "RMB"),
    price_min: urlGet("price_min", ""),
    price_max: urlGet("price_max", ""),
    price_currency: urlGet("price_currency", (isCustomer ? ((user?.currency || "USD") === "CAD" ? "CAD" : "USD") : (localStorage.getItem("quotesys_display_currency") || "USD"))),
  });
  const [sort, setSort] = useState(urlGet("sort", "category"));
  // 2026-02-26 · Post-import scope. When present, list is restricted to only
  // the just-imported item_numbers and a dismissible banner is shown.
  const importedItemsParam = urlGet("items", "");
  const [options, setOptions] = useState({ categories: [], subcategories: [], subcategory_names: [], brands: [], buyers: [], sellers: [] });
  const [importOpen, setImportOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  // 2026-02 · Selection persists 24h in localStorage per user (same pattern
  // as MarketSurveyPage) so refreshing / navigating away doesn't wipe an
  // in-progress batch. Key scoped to `user.email` to prevent leakage between
  // accounts on a shared browser.
  const SELECTION_STORAGE_KEY = `productsList.selected.v1::${(user?.email || "anon").toLowerCase()}`;
  const SELECTION_TTL_MS = 24 * 60 * 60 * 1000;
  const [selected, setSelected] = useState(() => {
    try {
      const raw = localStorage.getItem(SELECTION_STORAGE_KEY);
      if (!raw) return new Set();
      const { ids, expires } = JSON.parse(raw);
      if (!expires || Date.now() > expires) {
        localStorage.removeItem(SELECTION_STORAGE_KEY);
        return new Set();
      }
      return new Set(Array.isArray(ids) ? ids : []);
    } catch { return new Set(); }
  });
  // 2026-02-09 · "显示选择" scope — when active, the list is limited to
  // exactly the currently-selected item numbers (spans all pages, ignores
  // other scopes/filters). Auto-clears when the selection becomes empty.
  const [showSelected, setShowSelected] = useState(false);
  useEffect(() => {
    try {
      if (selected.size === 0) localStorage.removeItem(SELECTION_STORAGE_KEY);
      else localStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify({
        ids: Array.from(selected),
        expires: Date.now() + SELECTION_TTL_MS,
      }));
    } catch { /* storage disabled — in-memory still works */ }
  }, [selected, SELECTION_STORAGE_KEY]);
  // Re-hydrate when logged-in user switches (logout → login on shared PC).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SELECTION_STORAGE_KEY);
      if (!raw) { setSelected(new Set()); return; }
      const { ids, expires } = JSON.parse(raw);
      if (!expires || Date.now() > expires) { localStorage.removeItem(SELECTION_STORAGE_KEY); setSelected(new Set()); return; }
      setSelected(new Set(Array.isArray(ids) ? ids : []));
    } catch { setSelected(new Set()); }
  }, [SELECTION_STORAGE_KEY]);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);   // 2026-06 · 生成产品分享链接
  const [scannerOpen, setScannerOpen] = useState(false);         // 2026-10-22 · 手机扫码/OCR 入口
  const [tagValue, setTagValue] = useState("");
  // 2026-07-31 · Shared-tag dictionary for the batch picker. Loaded once
  // when the products list mounts so the dropdown is instant.
  const [tagDict, setTagDict] = useState([]);
  useEffect(() => {
    api.get("/tags").then(({ data }) => setTagDict(data.tags || [])).catch(() => {});
  }, []);
  const [dupOpen, setDupOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [tagManagerOpen, setTagManagerOpen] = useState(false);
  const [page, setPage] = useState(parseInt(urlGet("page", "1"), 10) || 1);
  // 2026-10-12 · 用户可切换每页显示 25 / 50 / 100。记在 localStorage，下次访问沿用。
  const PAGE_SIZE_OPTIONS = [25, 50, 100];
  const [pageSize, setPageSize] = useState(() => {
    const stored = parseInt(localStorage.getItem("quotesys_products_page_size") || "", 10);
    return PAGE_SIZE_OPTIONS.includes(stored) ? stored : 50;
  });
  const changePageSize = (n) => {
    setPageSize(n);
    localStorage.setItem("quotesys_products_page_size", String(n));
    setPage(1);
  };

  // Persist search / filters / sort / page to the URL so a subsequent product
  // edit + Back returns to the same view.
  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    for (const [k, v] of Object.entries(filters)) {
      if (k === "favorites" || k === "recent" || k === "mine" || k === "new_only") continue;  // handled separately below (boolean)
      if (v) params[k] = v;
    }
    // 2026-02 · Encode the "Show All" state so an empty product_type isn't
    // confused with "URL has no filter yet" on the next render.
    if (!filters.product_type && !filters.favorites && !filters.recent && !filters.mine && !filters.new_only) params.show_all = "1";
    // Favorites / Recent / Mine bits — explicit so refresh persists the tab state.
    if (filters.favorites) params.favorites = "1";
    if (filters.recent) params.recent = "1";
    if (filters.mine) params.mine = "1";
    if (filters.new_only) params.new_only = "1";
    if (sort && sort !== "created_desc") params.sort = sort;
    if (page > 1) params.page = String(page);
    // 2026-02-26 · preserve the post-import scope until the user clears it.
    if (importedItemsParam) params.items = importedItemsParam;
    setSearchParams(params, { replace: true });
  }, [search, filters, sort, page, setSearchParams, importedItemsParam]);
  const [total, setTotal] = useState(0);
  // Customer role: currency locked to the user's own preset (spec 2026-02 — no toggle).
  // Staff/factory: display currency is a UI preference (default USD).
  const [currency, setCurrency] = useState(
    isCustomer ? ((user?.currency || "USD") === "CAD" ? "CAD" : "USD") : (localStorage.getItem("quotesys_display_currency") || "USD")
  );
  const PAGE_SIZE = pageSize;

  const switchCurrency = (c) => {
    setCurrency(c);
    localStorage.setItem("quotesys_display_currency", c);
  };

  // Customer only: keep display currency in sync with the profile setting.
  // /auth/me may resolve AFTER first render, so we resync when user.currency arrives.
  useEffect(() => {
    if (isCustomer && user?.currency && user.currency !== currency) {
      setCurrency(user.currency === "CAD" ? "CAD" : "USD");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.currency, isCustomer]);

  const switchView = (v) => {
    setView(v);
    localStorage.setItem("quotesys_products_view", v);
  };

  // 2026-06 · 左侧「产品」二级菜单通过 URL 参数切换个人视图（收藏/新品/最近/我的），
  // 所以停留在本页时也要把 URL 的变化同步回 filters。比较后再 set，避免与
  // filters→URL 的同步形成回环。
  useEffect(() => {
    const fav = searchParams.get("favorites") === "1";
    const rec = searchParams.get("recent") === "1";
    const mineParam = searchParams.get("mine") === "1";
    const nw = searchParams.get("new_only") === "1";
    setFilters((f) => (f.favorites === fav && f.recent === rec && f.mine === mineParam && f.new_only === nw)
      ? f
      : { ...f, favorites: fav, recent: rec, mine: mineParam, new_only: nw });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchProducts = async (q, f = filters, pg = page) => {
    setLoading(true);
    try {
      // 2026-02-09 · When the user types a search query OR picks any
      // filter dropdown / date range / price range, ignore the ⭐我的收藏
      // / 🕒最近查看 / 👤我的产品 scope so results span the WHOLE catalog.
      // The scope toggles stay visible in the UI so clearing the query
      // restores the original view.
      // 2026-06 BUGFIX · This used to also blank ``date_from``/``date_to``
      // (left over from the removed 「近2周」 default preset), which meant the
      // creation-date filter silently cleared ITSELF and never changed the
      // results. Dates are always user-chosen now, so they are kept.
      const hasExplicitFilter = Boolean(
        q ||
        f.product_type || f.category || f.subcategory || f.brand ||
        f.buyer || f.seller ||
        f.date_from || f.date_to ||
        f.cost_min || f.cost_max || f.price_min || f.price_max
      );
      const activeFilters = hasExplicitFilter
        ? { ...f, favorites: false, recent: false, mine: false }
        : f;
      const params = { page: pg, page_size: PAGE_SIZE, sort, ...(q ? { search: q } : {}) };
      for (const [k, v] of Object.entries(activeFilters)) {
        // ``favorites`` / ``recent`` / ``mine`` are boolean flags — backend expects "1".
        if (k === "favorites") { if (v) params.favorites = "1"; continue; }
        if (k === "recent")    { if (v) params.recent = "1"; continue; }
        if (k === "mine")      { if (v) params.mine = "1"; continue; }
        if (k === "new_only")  { if (v) params.new_only = "1"; continue; }
        if (v) params[k] = v;
      }
      // 2026-02-26 · post-import scope wins over everything else so the user
      // sees exactly what they just imported.
      if (importedItemsParam) {
        params.items = importedItemsParam;
        // Drop favorites/recent/mine so backend does not intersect them.
        delete params.favorites; delete params.recent; delete params.mine;
      }
      // 2026-02-09 · "显示选择" wins similarly — hand the current selection
      // to backend as the items whitelist so the user can review exactly
      // what they picked, spanning across pages.
      if (showSelected && selected.size > 0) {
        params.items = Array.from(selected).join(",");
        delete params.favorites; delete params.recent; delete params.mine;
        delete params.search;   // selection is the primary intent here
      }
      const { data } = await api.get("/products", { params });
      setProducts(data.items);
      setTotal(data.total);
      // 2026-02 · Persist the filtered item_numbers so the ProductForm's ⬅➡
      // buttons walk *this* list instead of the whole DB. `pdl_scope_list`
      // takes precedence over the /neighbors API in ProductForm.
      try {
        sessionStorage.setItem("pdl_scope_list", JSON.stringify((data.items || []).map((p) => p.item_number)));
      } catch { /* storage disabled */ }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products/meta/options");
        setOptions(data);
      } catch (error) {
        console.warn("Failed to load filter options (non-blocking):", error);
      }
    })();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); fetchProducts(search, filters, 1); }, 350);
    return () => clearTimeout(timer);
  }, [search, filters, sort, importedItemsParam, showSelected, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps -- debounced fetch, fetchProducts is stable per render

  // 2026-02-09 · Auto-exit "显示选择" scope once the selection is empty
  // (user removed everything). Otherwise the empty items= would fetch nothing.
  useEffect(() => {
    if (showSelected && selected.size === 0) setShowSelected(false);
  }, [showSelected, selected]);

  // 2026-02 · Remember the exact list URL (with all query params) so
  // ProductForm's "← 产品列表" can return here regardless of how many
  // 上一个/下一个 clicks the user did inside the detail page. Depend on
  // `searchParams` so the return URL refreshes whenever the user changes
  // filters/sort/page.
  useEffect(() => {
    try {
      const path = window.location.pathname + window.location.search;
      sessionStorage.setItem("pdl_return_url", path);
      sessionStorage.setItem("pdl_return_label", "");
    } catch { /* storage disabled */ }
  }, [searchParams]);

  // 2026-02-26 · After the product list renders, if we returned here from a
  // detail edit page (sessionStorage `pdl_scroll_to`), scroll to that row and
  // briefly highlight it, then clear the marker so refreshing the list later
  // doesn't re-scroll.
  useEffect(() => {
    if (loading) return;
    let target;
    try { target = sessionStorage.getItem("pdl_scroll_to"); } catch {}
    if (!target) return;
    // Give the DOM a moment to paint all grid/table rows.
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-testid="product-card-${target}"], [data-testid="product-row-${target}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-brand", "ring-offset-2");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-brand", "ring-offset-2");
        }, 2200);
      }
      try { sessionStorage.removeItem("pdl_scroll_to"); } catch {}
    }, 60);
    return () => clearTimeout(timer);
  }, [loading, products]);

  const goToPage = (pg) => {
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const next = Math.min(Math.max(1, pg), totalPages);
    if (next === page) return;
    setPage(next);
    fetchProducts(search, filters, next);
    window.scrollTo({ top: 0, behavior: "smooth" });
    document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setFilter = (field, value) => {
    setFilters((f) => ({ ...f, [field]: value === "__all__" ? "" : value }));
  };

  const handleAddToQuote = async (itemNumber) => {
    try {
      const quote = await addToQuote(itemNumber);
      if (!quote) {
        // no active quote: open the square-tile picker
        setPendingItems([itemNumber]);
        setPickerOpen(true);
        return;
      }
      toast.success(`${t("quote.addedTo")} ${quote.quote_number}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const addItemsToQuote = async (items, quote) => {
    try {
      let used = quote || activeQuote;
      for (const item of items) {
        used = (await addToQuote(item, quote || undefined)) || used;
        quote = undefined; // only pass the picked quote on the first call
      }
      toast.success(`${items.length} ${t("products.addedToQuote")} ${used.quote_number}`);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handlePickQuote = async (quote) => {
    setPickerOpen(false);
    if (!pendingItems.length) return;
    await addItemsToQuote(pendingItems, quote);
    setPendingItems([]);
  };

  // ----- batch selection -----
  const allSelected = products.length > 0 && products.every((p) => selected.has(p.item_number));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(products.map((p) => p.item_number)));
  const toggleSelect = (itemNumber) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(itemNumber)) next.delete(itemNumber);
      else next.add(itemNumber);
      return next;
    });
  };

  const batchAddToQuote = () => {
    const items = [...selected];
    if (!items.length) return;
    if (!activeQuote) {
      setPendingItems(items);
      setPickerOpen(true);
      return;
    }
    addItemsToQuote(items);
  };

  const printSelectedLabels = async () => {
    const items = [...selected];
    if (!items.length) return;
    setPrinting(true);
    try {
      // 2026-06-26 · Label 币种跟随本页选择的币种（US$/C$），不再用账号默认币种。
      const { data } = await api.post("/products/labels", { item_numbers: items, currency }, { responseType: "blob", timeout: 60000 });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.exportError"));
    } finally {
      setPrinting(false);
    }
  };

  const exportGridPdf = async () => {
    const items = [...selected];
    if (!items.length) return;
    try {
      const { data } = await api.post(
        "/products/grid-pdf",
        { item_numbers: items, title: t("products.gridPdfTitle") },
        { responseType: "blob", timeout: 120000 },
      );
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.exportError"));
    }
  };

  // Customer-template Excel export moved to the Quote detail page (per user request 2026-02):
  // it now runs off the Quote's linked customer, so the correct template is picked
  // automatically without asking the user to choose customer + template on the Products page.

  const deleteSelected = async () => {
    const items = [...selected];
    setDeleting(true);
    try {
      // 2026-10-12 · 必须 encodeURIComponent — 某些 item_number 带空格（例如
      // "L23-BD009 L24-BD146" 这类广交会多编号合并产品），不编码会把 URL 打断
      // 导致 404 删不掉。
      await Promise.all(items.map((i) => api.delete(`/products/${encodeURIComponent(i)}`)));
      toast.success(`${t("products.deleted")}: ${items.length}`);
      setDeleteDialogOpen(false);
      setSelected(new Set());
      fetchProducts(search);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const applyBatchTag = async () => {
    const items = [...selected];
    const name = tagValue.trim();
    if (!name) return;
    try {
      // 2026-07-31 · Switched from the per-user "my-tag" endpoint to the
      // shared /tags/batch endpoint so the tag actually shows up in the
      // Tag Management page and increments the tag's product count.
      const { data } = await api.post("/tags/batch", {
        item_numbers: items,
        tag_name: name,
        action: "add",
      });
      toast.success(`${t("products.tagApplied")}: ${data.updated ?? items.length}`);
      setTagDialogOpen(false);
      setTagValue("");
      setSelected(new Set());
      fetchProducts(search);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Batch copy (2026-02 · admin + export only). New items are auto-numbered
  // ``{user.name}-NNN`` server-side and appear at the top of the list on refresh.
  const batchCopyProducts = async () => {
    const items = [...selected];
    if (!items.length) return;
    try {
      const { data } = await api.post("/products/copy", { item_numbers: items });
      const created = (data?.copies || []).map((c) => c.new_item_number).join(", ");
      toast.success(`${t("products.copied")}: ${data?.count || 0}${created ? " · " + created : ""}`);
      setSelected(new Set());
      fetchProducts(search);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Bulk-favorite (2026-02) — flip every selected item's 我的收藏 flag ON.
  const batchAddFavorites = async () => {
    const items = [...selected];
    if (!items.length) return;
    try {
      const { data } = await api.post("/products/favorites/bulk", { item_numbers: items, action: "add" });
      toast.success(`${t("products.addedToFavorites")}: ${data?.changed || 0}`);
      setSelected(new Set());
      // Refresh both the list (to update the star icons) and the count badge.
      fetchProducts(search);
      window.dispatchEvent(new Event("favorites-changed"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };




  return (
    <div className="animate-fade-in-up">
      <QuotePicker open={pickerOpen} onOpenChange={setPickerOpen} onSelect={handlePickQuote} />
      {/* 2026-10-22 · 手机端扫码 / 拍照 OCR / 手输 — 识别到后直接写进搜索框过滤列表 */}
      <BarcodeScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onDetect={(code) => {
          setSearch(code);
          setShowSelected(false);
          goToPage(1);
          toast.success(`${t("scanner.filled") || "已填入搜索框"}: ${code}`);
        }}
      />
      {importedItemsParam && (
        <div
          className="mb-4 flex items-center gap-3 bg-emerald-50 border border-emerald-300 rounded-md px-4 py-2.5"
          data-testid="just-imported-banner"
        >
          <CheckCircle2 size={16} className="text-emerald-700" />
          <span className="text-sm font-medium">
            {t("products.justImportedBanner")
              .replace("{n}", String(importedItemsParam.split(",").filter(Boolean).length))}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setSearchParams((p) => { const np = new URLSearchParams(p); np.delete("items"); return np; }, { replace: true }); }}
            data-testid="clear-just-imported-button"
            className="ml-auto rounded-md"
          >
            {t("products.clearImportedFilter")}
          </Button>
        </div>
      )}
      <ImportExcelDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => fetchProducts(search)} />
      <DuplicateFinderDialog open={dupOpen} onOpenChange={setDupOpen} onDeleted={() => fetchProducts(search)} />
      <TagManagerDialog open={tagManagerOpen} onOpenChange={setTagManagerOpen} onChanged={() => fetchProducts(search)} />
      {/* Unified toolbar (2026-02 per user spec): 产品列表 title + batch actions LEFT,
          search + filters MIDDLE, action buttons + currency toggle RIGHT — all one flex-wrap row. */}
      {/* 2026-06 · 紧凑双行工具栏（用户规格）：第一行 = 标题 + 报价单 + 选择/批量
          + 搜索 + 新建/导入/返回；第二行 = 全部筛选。个人视图（收藏/新品/最近/我的）
          已移到左侧「产品」二级菜单，避免重复。 */}
      <div className="space-y-2 mb-3" data-testid="products-toolbar">
      <div className="flex items-center gap-1.5 flex-nowrap min-w-0">
        <h1 className="font-heading text-base font-bold tracking-tight text-foreground whitespace-nowrap hidden sm:block" data-testid="products-title">
          {t("products.title")}
        </h1>
        {activeQuote && (
          <span className="flex items-center gap-1.5 text-sm whitespace-nowrap text-muted-foreground" data-testid="selecting-for-quote-chip">
            <span className="text-border">|</span>
            <FileText size={14} className="text-brand" />
            <span className="font-mono font-semibold text-foreground">{activeQuote.quote_number}</span>
          </span>
        )}
        {/* Selection + Actions: shown for staff AND customer (2026-02 spec —
            customer can select products to Set Tag or Print Grid PDF). Factory
            role remains fully read-only. */}
        {!isFactory && (
          <div className="hidden md:flex items-center gap-1.5 flex-nowrap">
            <Button variant="outline" size="sm" onClick={toggleSelectAll} data-testid="products-select-all-button" className="rounded-md h-9 whitespace-nowrap">
              {allSelected ? t("products.deselectAll") : t("products.selectAll")}
            </Button>
            <span className="font-mono text-xs text-muted-foreground" data-testid="products-selected-count">
              {selected.size}
            </span>
            {/* 2026-02 · Clear-all-selection button. Only rendered when at least
                one row is ticked, so it doesn't clutter the toolbar. Mirrors
                the "清空选择" affordance on the Market Survey page. */}
            {selected.size > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelected(new Set())}
                data-testid="products-clear-selection-button"
                className="rounded-md h-9 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                {t("products.clearSelection") || "清空选择"}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" disabled={selected.size === 0} data-testid="products-action-button" className="rounded-md h-9 bg-brand hover:bg-brand-hover transition-colors">
                  {t("products.actions")} <ChevronDown size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {/* Add-to-Quote / Print-Labels: staff only (customer can't build quotes).
                    2026-06 · 采购 keeps ONLY 设置标签 + 复制产品 (user spec). */}
                {!isCustomer && !isBuyerRole && (
                  <>
                    <DropdownMenuItem onClick={batchAddToQuote} data-testid="action-add-to-quote">
                      <FilePlus2 size={14} /> {t("products.addToQuote")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={printSelectedLabels} disabled={printing} data-testid="action-print-labels">
                      <Printer size={14} /> {t("quote.printLabels")}
                    </DropdownMenuItem>
                  </>
                )}
                {/* Set Tag: any role — routes to that user's private product_tags row. */}
                <DropdownMenuItem onClick={() => setTagDialogOpen(true)} data-testid="action-set-tag">
                  <Tag size={14} /> {t("products.setTag")}
                </DropdownMenuItem>
                {/* Bulk favorite — 2026-02 spec. Flips every selected row's
                    我的收藏 flag ON in one call. Available for any authenticated role. */}
                {!isBuyerRole && (
                <DropdownMenuItem onClick={batchAddFavorites} data-testid="action-bulk-favorite">
                  <Star size={14} /> {t("products.bulkFavorite")}
                </DropdownMenuItem>
                )}
                {!isBuyerRole && (
                <DropdownMenuItem onClick={exportGridPdf} data-testid="action-grid-pdf">
                  <LayoutGrid size={14} /> {t("products.gridPdf")}
                </DropdownMenuItem>
                )}
                {/* Copy product (2026-02): admin + export; 2026-06 · 采购/采购经理 too. */}
                {["admin", "export", "buyer", "procurement_manager"].includes(user?.role) && (
                  <DropdownMenuItem onClick={batchCopyProducts} data-testid="action-copy-products">
                    <Copy size={14} /> {t("products.copy")}
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem onClick={() => setLinkDialogOpen(true)} data-testid="action-generate-link">
                    <Link2 size={14} /> {t("plink.action")}
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)} data-testid="action-delete-products" className="text-destructive focus:text-destructive">
                    <Trash2 size={14} /> {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        <div className="relative flex-1 min-w-[120px] max-w-[420px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("products.searchPlaceholder")}
            data-testid="products-search-input"
            className="pl-9 pr-10 rounded-md h-9"
          />
          {/* 2026-10-22 · 手机端扫码按钮（搜索框右内嵌，md 以上隐藏） */}
          <button
            type="button"
            onClick={() => setScannerOpen(true)}
            className="md:hidden absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-brand hover:bg-brand/10 active:bg-brand/20 transition-colors"
            aria-label={t("scanner.title") || "扫描货号"}
            data-testid="products-scan-button"
          >
            <ScanBarcode size={16} />
          </button>
        </div>
        {/* 2026-06 · 手机端筛选抽屉（桌面那一行筛选控件在手机上放不下） */}
        <MobileProductFilters
          filters={filters}
          setFilter={setFilter}
          options={options}
          currency={currency}
          switchCurrency={switchCurrency}
          showCurrency={!isCustomer}
          total={total}
          onShowAll={() => { setSearch(""); setShowSelected(false); setFilters((f) => ({ ...f, product_type: "", category: "", subcategory: "", brand: "", favorites: false, recent: false, mine: false })); }}
        />
        {/* 2026-10-12 · 手机端 List ↔ Grid 视图切换（桌面那组在手机被隐藏，这里补一个紧凑版） */}
        {!isCustomer && (
          <div className="md:hidden flex items-center rounded-md border border-border p-0.5" data-testid="m-view-toggle">
            <button
              type="button"
              onClick={() => switchView("list")}
              data-testid="m-view-list-button"
              aria-label={t("products.viewList") || "List"}
              className={`px-2 py-1.5 rounded-md transition-colors ${view === "list" ? "bg-brand text-white" : "text-muted-foreground"}`}
            >
              <List size={14} />
            </button>
            <button
              type="button"
              onClick={() => switchView("grid")}
              data-testid="m-view-grid-button"
              aria-label={t("products.viewGrid") || "Grid"}
              className={`px-2 py-1.5 rounded-md transition-colors ${view === "grid" ? "bg-brand text-white" : "text-muted-foreground"}`}
            >
              <LayoutGrid size={14} />
            </button>
          </div>
        )}
        {/* 新建产品 / 导入Excel / 返回报价单 —— 固定在第一行右侧，任何宽度下不换行。 */}
        {!isReadOnly && (
          <Button asChild size="sm" data-testid="new-product-button" className="rounded-md h-9 whitespace-nowrap bg-brand hover:bg-brand-hover transition-colors">
            <Link to="/products/new">
              <Plus size={15} /> {t("products.new")}
            </Link>
          </Button>
        )}
        {!isReadOnly && (
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="import-excel-button" className="rounded-md h-9 whitespace-nowrap hidden md:inline-flex">
            <FileSpreadsheet size={15} className="text-green-700" /> {t("products.importExcel")}
          </Button>
        )}
        {activeQuote && (
          <Button size="sm" variant="outline" onClick={() => navigate("/quotes")} data-testid="back-to-quote-button" className="rounded-md h-9 whitespace-nowrap">
            {t("quote.backToQuote")}
          </Button>
        )}
      </div>

      {/* 第二行：筛选条件（最多两行，控件一律 h-9）
          2026-06 · 手机端只保留搜索框，筛选/货币/视图/密度全部隐藏（用户要求页面干净）。 */}
      <div className="hidden md:flex flex-wrap items-center gap-1.5" data-testid="products-filter-row">
        {/* 2026-02-09 · 显示选择 — restricts the list to exactly the
            currently-selected item_numbers (across pages). Disabled with a
            hint when the selection is empty; clicking again toggles back
            to the previous scope. */}
        <Button
          variant={showSelected ? "default" : "outline"}
          size="sm"
          onClick={() => { setSearch(""); setShowSelected((v) => !v); }}
          disabled={selected.size === 0}
          data-testid="products-show-selected-button"
          className={`h-9 rounded-md gap-1 ${showSelected ? "bg-brand hover:bg-brand-hover text-white" : ""}`}
          title={selected.size === 0 ? (t("products.showSelectedEmptyHint") || "请先勾选产品") : (t("products.showSelected") || "显示选择")}
        >
          <CheckCircle2 size={14} /> {t("products.showSelected") || "显示选择"}
          {selected.size > 0 && (
            <span
              data-testid="show-selected-count-badge"
              className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${showSelected ? "bg-white/25 text-white" : "bg-brand/15 text-brand"}`}
            >
              {selected.size}
            </span>
          )}
        </Button>
        {/* Show All — 2026-02 spec 「只有按了这个按钮才可以在所有的产品中查询」.
            Clears BOTH the default 我的收藏 filter and any product_type so users
            can search the entire catalog.
            2026-02-09 · Also clears the search box so this button always
            returns the user to a clean "all products" browsing state, no
            matter what mode they were in before. */}
        <Button
          variant={filters.product_type === "" && !filters.favorites && !filters.recent && !filters.mine && !showSelected ? "default" : "outline"}
          size="sm"
          onClick={() => { setSearch(""); setShowSelected(false); setFilters((f) => ({ ...f, product_type: "", favorites: false, recent: false, mine: false })); }}
          data-testid="products-show-all-button"
          className="h-9 rounded-md gap-1"
          title={t("products.showAll")}
        >
          {t("products.showAll")}
        </Button>
        <ProductFilters
          filters={filters}
          setFilter={setFilter}
          options={options}
          displayCurrency={currency}
        />
        {/* Display currency toggle — moved next to 新建产品 (2026-07-31). */}
        {!isCustomer && (
          <div className="flex items-center rounded-md border border-border p-0.5" data-testid="currency-toggle">
            {["USD", "CAD"].map((c) => (
              <button
                key={c}
                onClick={() => switchCurrency(c)}
                data-testid={`currency-${c.toLowerCase()}-button`}
                className={`px-2.5 py-1.5 rounded-md font-mono text-xs font-bold transition-colors ${currency === c ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground"}`}
              >
                {c === "USD" ? "US$" : "C$"}
              </button>
            ))}
          </div>
        )}
        {/* 2026-02-09 · View toggle — list ↔ grid. Persisted in localStorage
            via ``switchView``. Hidden for customer role (locked to grid). */}
        {!isCustomer && (
          <div className="flex items-center rounded-md border border-border p-0.5" data-testid="view-toggle">
            <button
              type="button"
              onClick={() => switchView("list")}
              data-testid="view-list-button"
              title={t("products.viewList") || "列表视图"}
              className={`px-2.5 py-1.5 rounded-md transition-colors ${view === "list" ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground"}`}
            >
              <List size={14} />
            </button>
            <button
              type="button"
              onClick={() => switchView("grid")}
              data-testid="view-grid-button"
              title={t("products.viewGrid") || "网格视图"}
              className={`px-2.5 py-1.5 rounded-md transition-colors ${view === "grid" ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground"}`}
            >
              <LayoutGrid size={14} />
            </button>
          </div>
        )}
        {/* 2026-02-09 · Density toggle for grid view — 紧凑 packs more cards/row
            (image + SKU only), 舒适 keeps the full detail card. Only visible
            when grid view is active. */}
        {view === "grid" && (
          <div className="flex items-center rounded-md border border-border p-0.5" data-testid="density-toggle">
            <button
              type="button"
              onClick={() => switchDensity("compact")}
              data-testid="density-compact-button"
              title={t("products.densityCompact") || "紧凑"}
              className={`px-2.5 py-1.5 rounded-md text-xs font-mono font-bold transition-colors ${density === "compact" ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground"}`}
            >
              {t("products.densityCompact") || "紧凑"}
            </button>
            <button
              type="button"
              onClick={() => switchDensity("comfortable")}
              data-testid="density-comfortable-button"
              title={t("products.densityComfortable") || "舒适"}
              className={`px-2.5 py-1.5 rounded-md text-xs font-mono font-bold transition-colors ${density === "comfortable" ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground"}`}
            >
              {t("products.densityComfortable") || "舒适"}
            </button>
          </div>
        )}
      </div>
      </div>

      {/* batch delete confirm dialog (admin only) */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="batch-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">{t("common.delete")} ({selected.size})</DialogTitle>
            <DialogDescription className="text-xs">{t("products.deleteConfirm")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-md">{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={deleteSelected} disabled={deleting} data-testid="batch-delete-confirm-button" className="rounded-md">
              {deleting ? "..." : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-07-31 · Batch "设置 Tag" now uses the shared picker (with search
          + oldest-first order + real selection count) instead of a free-text
          Input. Writes to Product.tags[] via /api/tags/batch. */}
      <ProductLinkDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        itemNumbers={[...selected]}
      />
      <TagPickerDialog
        open={tagDialogOpen}
        onOpenChange={setTagDialogOpen}
        selectedCount={selected.size}
        onConfirm={async (name) => {
          if (!name) return;
          try {
            const { data } = await api.post("/tags/batch", {
              item_numbers: [...selected],
              tag_name: name,
              action: "add",
            });
            toast.success(`${t("products.tagApplied") || "已应用标签"}: ${data.updated ?? selected.size}`);
            setTagDialogOpen(false);
            setSelected(new Set());
            fetchProducts(search);
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
          }
        }}
      />

      <div className="md:hidden">
        {/* 2026-10-12 · 手机端支持 Grid ↔ List 切换 + 批量选择（和桌面一致） */}
        {view === "grid" ? (
          <ProductsGrid
            products={products}
            loading={loading}
            onAddToQuote={handleAddToQuote}
            selected={selected}
            onToggleSelect={toggleSelect}
            readOnly={isReadOnly}
            noEdit={isCustomer}
            canSelect={!isFactory}
            currency={currency}
            density={density}
          />
        ) : (
          <MobileProductsList
            products={products}
            loading={loading}
            currency={currency}
            canUpload={!isReadOnly}
            onPhotoUpdated={(item, url) => setProducts((prev) => prev.map((p) => (p.item_number === item ? { ...p, photo_url: url } : p)))}
            selected={selected}
            onToggleSelect={toggleSelect}
            canSelect={!isFactory}
          />
        )}
      </div>
      <div className="hidden md:block">
        {view === "grid" ? (
          <ProductsGrid products={products} loading={loading} onAddToQuote={handleAddToQuote} selected={selected} onToggleSelect={toggleSelect} readOnly={isReadOnly} noEdit={isCustomer} canSelect={!isFactory} currency={currency} density={density} />
        ) : (
          <ProductsTable products={products} loading={loading} selected={selected} onToggleSelect={toggleSelect} readOnly={isReadOnly} currency={currency} />
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="font-mono text-xs text-muted-foreground" data-testid="products-records-count">
          {total} {t("products.recordsFound")}
        </p>
        <div className="flex items-center gap-3">
          {/* 2026-10-12 · 每页显示数量下拉 */}
          <div className="hidden md:flex items-center gap-1.5" data-testid="products-page-size">
            <span className="text-xs text-muted-foreground">{t("products.perPage") || "每页"}</span>
            <Select value={String(pageSize)} onValueChange={(v) => changePageSize(parseInt(v, 10))}>
              <SelectTrigger className="h-8 w-[72px] text-xs" data-testid="products-page-size-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)} data-testid={`products-page-size-${n}`}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {total > PAGE_SIZE && (
            <div className="hidden md:flex items-center gap-1" data-testid="products-pagination">
              <Button variant="outline" size="sm" className="rounded-md h-8" disabled={page <= 1}
                onClick={() => goToPage(page - 1)} data-testid="pagination-prev-button">
                <ChevronLeft size={14} />
              </Button>
              <span className="font-mono text-xs px-3" data-testid="pagination-info">
                {page} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
              </span>
              <Button variant="outline" size="sm" className="rounded-md h-8" disabled={page >= Math.ceil(total / PAGE_SIZE)}
                onClick={() => goToPage(page + 1)} data-testid="pagination-next-button">
                <ChevronRight size={14} />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* 2026-06 · 手机端翻页 —— 44px 大按钮，单手可点 */}
      {total > PAGE_SIZE && (
        <div className="md:hidden mt-3 flex items-center gap-2" data-testid="m-products-pagination">
          <Button variant="outline" disabled={page <= 1} onClick={() => goToPage(page - 1)}
            data-testid="m-pagination-prev" className="flex-1 h-12 rounded-md">
            <ChevronLeft size={16} />
          </Button>
          <span className="font-mono text-xs px-2 shrink-0" data-testid="m-pagination-info">
            {page} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
          </span>
          <Button variant="outline" disabled={page >= Math.ceil(total / PAGE_SIZE)} onClick={() => goToPage(page + 1)}
            data-testid="m-pagination-next" className="flex-1 h-12 rounded-md">
            <ChevronRight size={16} />
          </Button>
        </div>
      )}

      {/* 2026-10-12 · 手机端批量操作栏 —— selected.size > 0 时固定在屏幕底部，
          汇集所有批量操作（和桌面 Actions 下拉保持一致）。 */}
      {!isFactory && selected.size > 0 && (
        <div
          className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-card/95 backdrop-blur-md shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-3 py-2 flex items-center gap-2"
          data-testid="m-selection-bar"
        >
          <span className="font-mono text-xs font-bold shrink-0" data-testid="m-selected-count">
            {selected.size}
          </span>
          <Button
            variant={showSelected ? "default" : "outline"}
            size="sm"
            onClick={() => { setSearch(""); setShowSelected((v) => !v); }}
            data-testid="m-products-show-selected-button"
            className={`h-9 rounded-md px-2 text-xs ${showSelected ? "bg-brand hover:bg-brand-hover text-white" : ""}`}
          >
            <CheckCircle2 size={13} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelected(new Set())}
            className="h-9 rounded-md px-2 text-xs text-destructive"
            data-testid="m-products-clear-selection-button"
          >
            {t("products.clearSelection") || "清空"}
          </Button>
          <div className="flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="rounded-md h-9 bg-brand hover:bg-brand-hover text-white"
                data-testid="m-products-action-button">
                {t("products.actions")} <ChevronDown size={13} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top">
              {!isCustomer && !isBuyerRole && (
                <>
                  <DropdownMenuItem onClick={batchAddToQuote} data-testid="m-action-add-to-quote">
                    <FilePlus2 size={14} /> {t("products.addToQuote")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={printSelectedLabels} disabled={printing} data-testid="m-action-print-labels">
                    <Printer size={14} /> {t("quote.printLabels")}
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem onClick={() => setTagDialogOpen(true)} data-testid="m-action-set-tag">
                <Tag size={14} /> {t("products.setTag")}
              </DropdownMenuItem>
              {!isBuyerRole && (
                <DropdownMenuItem onClick={batchAddFavorites} data-testid="m-action-bulk-favorite">
                  <Star size={14} /> {t("products.bulkFavorite")}
                </DropdownMenuItem>
              )}
              {!isBuyerRole && (
                <DropdownMenuItem onClick={exportGridPdf} data-testid="m-action-grid-pdf">
                  <LayoutGrid size={14} /> {t("products.gridPdf")}
                </DropdownMenuItem>
              )}
              {["admin", "export", "buyer", "procurement_manager"].includes(user?.role) && (
                <DropdownMenuItem onClick={batchCopyProducts} data-testid="m-action-copy-products">
                  <Copy size={14} /> {t("products.copy")}
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem onClick={() => setLinkDialogOpen(true)} data-testid="m-action-generate-link">
                  <Link2 size={14} /> {t("plink.action")}
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)} data-testid="m-action-delete-products"
                  className="text-destructive focus:text-destructive">
                  <Trash2 size={14} /> {t("common.delete")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}
